<template>
  <VueTagsInput
    v-model="inputValue"
    :tags="displayTags"
    :disabled="disabled"
    :placeholder="placeholder"
    :max-tags="limit"
    :add-on-key="addOnKey"
    :add-on-blur="true"
    @before-adding-tag="onBeforeAddingTag"
    @before-deleting-tag="onBeforeRemovingTag"
    @tags-changed="onTagsUpdated"
    @update:modelValue="onInput"
  />
</template>

<script lang="ts" setup>
import VueTagsInput, { AddTagEvent, DeleteTagEvent, Tag } from '@sipec/vue3-tags-input';
import HtmlFormatter from '@/shared/services/htmlFormatter';
import { PropType, ref, watch, computed } from 'vue';
import { useVModel } from '@vueuse/core';
import Toast from '@shared/services/toast';

const props = defineProps({
  elementId: {
    type: String,
  },
  placeholder: {
    type: String,
  },
  disabled: {
    type: Boolean,
  },
  beforeAddingTag: {
    type: Function as PropType<() => boolean>,
  },
  validation: {
    type: Function as PropType<(input: string) => boolean>,
  },
  validationErrorMessage: {
    type: String,
    default: '',
  },
  addTagsOnSpace: {
    type: Boolean,
    default: false,
  },
  limit: {
    type: Number,
    default: 0,
  },
  modelValue: {
    type: Array as PropType<string[]>,
    default: [],
  },
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void;
  (e: 'text-updated', value: any): void;
  (e: 'tags-updated'): void;
}>();
const model = useVModel(props, 'modelValue', emit);

const inputValue = ref<string>('');
const displayTags = ref<{ text: string; tiClasses?: string[] }[]>([]);

const addOnKey = computed(() => (props.addTagsOnSpace ? [13, ',', ' '] : [13, ',']));

// Sync with modelValue on mount and updates
watch(
  model,
  (values: string[], oldValues: string[]) => {
    if (!values || arraysEqual(values, oldValues)) return;

    displayTags.value = values.map((tagText: string) => ({ text: tagText }));
  },
  { immediate: true },
);

function onBeforeAddingTag(evt: AddTagEvent) {
  const rawValue = evt.tag.text;

  evt.tag.text = HtmlFormatter.escapeHtmlCharacters(rawValue);

  // Perform validation here to control whether the tag is added
  if (props.validation && !props.validation(evt.tag.text)) {
    if (props.validationErrorMessage) {
      Toast.showError(props.validationErrorMessage); // Use custom error message
    }
    return; // Prevent adding the tag
  }

  if (!props.beforeAddingTag || props.beforeAddingTag()) {
    evt.addTag();
  }
}

function onBeforeRemovingTag(evt: DeleteTagEvent) {
  evt.deleteTag();
}

function onTagsUpdated(newTags: Tag[]) {
  model.value = newTags.map((tag: Tag) => tag.text);
  emit('tags-updated');
}

function onInput(newValue: string) {
  emit('text-updated', newValue);
}

function arraysEqual(a: any[], b: any[]) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((item, i) => item === b[i]);
}
</script>

<style scoped lang="scss">
// Root wrapper (optional, for consistency with old class)
:deep(.vue-tags-input) {
  position: relative;
}

// Main input container
:deep(.ti-input) {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 0.5em;
  background: #fff;
  border: 1px solid #dbdbdb;
  border-radius: 0.25em;
  box-shadow: inset 0 1px 2px rgba(10, 10, 10, 0.1);

  &.ti-focus {
    outline: none;
    border-color: #3273dc;
    box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
  }

  input {
    flex: 1;
    background: transparent;
    border: none;
    &:focus-visible {
      outline: none;
    }
  }
}

// Tag list (ul.ti-tags)
:deep(.ti-tags) {
  display: flex;
  flex-wrap: wrap;
  margin: 0;
  padding: 0;
  list-style: none;
}

// Individual tag
:deep(.ti-tag) {
  display: flex;
  align-items: center;
  margin-right: 0.3em;
  margin-bottom: 0.2rem;
  padding: 0.5em 0.6em;
  font-size: 0.8em;
  font-weight: 400;
  border-radius: 0.25em;
  color: #212529;
  background-color: #f0f1f2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.ti-valid {
    // Already set above, but explicit for clarity
    color: #212529;
    background-color: #f0f1f2;
  }

  &.ti-invalid {
    background-color: #e54d42; // Optional: Red for invalid tags
  }

  // Tag content (text)
  .ti-content {
    display: flex;
    align-items: center;

    .ti-tag-center {
      display: inline-block; // Inline with actions
      span {
        line-height: 1; // Match old line-height
      }
    }
  }

  // Actions (delete cross)
  .ti-actions {
    display: flex;
    align-items: center;
    margin-left: 0.4em; // Space between text and cross

    .ti-icon-close {
      font-size: 1em; // Adjust size to fit inline
      cursor: pointer;
      color: #4a4a4a; // Match your old cross color
      speak: none; // From component’s font setup
      font-style: normal;
      font-weight: normal;
      font-family: 'icomoon' !important; // Ensure icon font applies
      line-height: 1;

      // Replace with custom X if needed (instead of icomoon)
      &:before {
        content: '\e901'; // Default close icon from icomoon
      }

      // Optional: Custom X styling (if icomoon isn’t desired)
      /* content: ''; // Remove icomoon content
      width: 0.75em;
      height: 0.75em;
      position: relative;
      display: inline-block;
      &:before, &:after {
        content: '';
        position: absolute;
        width: 75%;
        height: 2px;
        background: #4a4a4a;
        left: 0.15em;
        top: 50%;
      }
      &:before {
        transform: rotate(45deg);
      }
      &:after {
        transform: rotate(-45deg);
      } */
    }

    .ti-icon-undo {
      display: none; // Hidden by default unless editing
    }
  }
}

// Input wrapper
:deep(.ti-new-tag-input-wrapper) {
  flex: 1;
  padding: 0; // Remove extra padding to align with tags
}
</style>
